import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import VendorHeader from "./../component/VendorHeader";
import VendorFooter from "./../component/VendorFooter";
import countryGroups from "../assets/country-groups.json";
import { API_BASE_URL } from "../utils/app_url";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import Sidebar from "../component/Sidebar";
import { NotificationManager } from "react-notifications";
import AiEnhancePanel from "../component/AiEnhancePanel";

const deleteIcon = require("./../assets/img/delete-icon.svg").default;

// ---------------------------------------------------------------------------
// AddProduct — main page
// ---------------------------------------------------------------------------
const AddProduct = () => {
  const { user, storeData, setStoreData } = useAuth();
  const navigate = useNavigate();
  const { selectedLanguage, langJson } = useLanguage();
  const imageBtnRef = useRef();

  const [addFormData, setAddFormData] = useState({
    article_no: "", order_no: "", article_name: "", brand: "", color: "",
    article_description: "", unit_price: "", currency: "₹", price: "",
    article_type: "public", size: "", original_price: "", main_category: "",
    sub_category: "", show_category: "", made_in: "DE",
    stock_type: "in_stock", estimated_delivery_date: "", estimated_delivery_days: "",
    ships_from: "", eligible_for: "", details: "",
  });

  const [dropdownLang, setDropdownLang] = useState(selectedLanguage || "en");
  const [sizeArray, setSizeArray] = useState([]);
  const [signupStatus, setSignupStatus] = useState("");
  const [sizeError, setSizeError] = useState("");
  const [imageError, setImageError] = useState("");
  const [videoError, setVideoError] = useState("");
  const [addBtnDis, setAddBtnDis] = useState(false);
  const [subCategoryList, setSubCategoryList] = useState([]);
  const [showCategoryList, setShowCategoryList] = useState([]);
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [zoomModalOpen, setZoomModalOpen] = useState(false);
  const [zoomImageIndex, setZoomImageIndex] = useState(0);
  const videoBtnRef = useRef();
  const currency = ["₹"];
  const allowedVideoExtensions = ["mp4", "webm", "mov", "avi", "mkv"];

  useEffect(() => { setDropdownLang(selectedLanguage || "en"); }, [selectedLanguage]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAddFormData((prev) => ({ ...prev, [name]: value }));
  };

  const addSize = () => {
    if (!addFormData.size) { setSizeError(langJson?.sizeisrequired || "Size is required."); return; }
    const exists = sizeArray.findIndex((item) => item?.size === addFormData.size);
    if (exists === -1) setSizeArray((prev) => [...prev, { size: addFormData.size, quantity: 1 }]);
    setSizeError("");
    setAddFormData((prev) => ({ ...prev, size: "" }));
  };

  const removeSize = (i) => setSizeArray((prev) => prev.filter((_, idx) => idx !== i));
  const onQuantityChange = (e, i) => {
    const arr = [...sizeArray]; arr[i].quantity = e ? e.target.value : 0; setSizeArray(arr);
  };

  const fetchSubCategory = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}products/product-categories`, {
        method: "GET", headers: { "Content-Type": "application/json", Authorization: `Bearer ${user.authorisation.token}` },
      });
      const data = await res.json();
      if (data.res === true) setSubCategoryList(data.data);
    } catch (e) { console.error(e); }
  };

  const fetchShowSubCategory = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}products/categories`, {
        method: "GET", headers: { "Content-Type": "application/json", Authorization: `Bearer ${user.authorisation.token}` },
      });
      const data = await res.json();
      if (data.res === true) setShowCategoryList(data.data);
    } catch (e) { console.error(e); }
  };

  const fetchStoreData = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}stores/dashboard`, {
        method: "GET",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${user.authorisation.token}` },
      });
      const data = await res.json();
      if (data.res === true) {
        setStoreData({
          total_products: data.data?.total_products,
          active_plan: data.data?.store?.active_plan,
        });
      }
    } catch (e) { console.error(e); }
  };

  const allowedExtensions = ["jpg", "jpeg", "png", "webp", "avif"];

  const handleImageFiles = (files) => {
    Array.from(files).forEach((file) => {
      const ext = file.name.split(".").pop().toLowerCase();
      if (allowedExtensions.includes(ext)) {
        setImages((prev) => [...prev, file]);
      } else {
        setImageError(langJson?.invalid_file_format_Please_upload_a_valid__image_file || "Invalid file format.");
      }
    });
  };

  const handleDrop = (e) => { e.preventDefault(); if (e.dataTransfer.files.length > 0) handleImageFiles(e.dataTransfer.files); };
  const handleFileInput = (e) => { if (e.target.files.length > 0) handleImageFiles(e.target.files); };
  const handleRemoveImage = (i) => setImages((prev) => prev.filter((_, idx) => idx !== i));
  const handleReplaceImage = (i, f) => setImages((prev) => prev.map((img, idx) => (idx === i ? f : img)));

  const handleVideoFiles = (files) => {
    Array.from(files).forEach((file) => {
      const ext = file.name.split(".").pop().toLowerCase();
      const maxSize = 50 * 1024 * 1024;
      if (!allowedVideoExtensions.includes(ext)) {
        setVideoError(langJson?.invalid_file_format_Please_upload_a_valid__image_file || "Invalid video format. Allowed: MP4, WebM, MOV, AVI, MKV");
      } else if (file.size > maxSize) {
        setVideoError("Video size must be less than 50MB");
      } else {
        setVideos((prev) => [...prev, file]);
        setVideoError("");
      }
    });
  };

  const handleVideoDrop = (e) => { e.preventDefault(); if (e.dataTransfer.files.length > 0) handleVideoFiles(e.dataTransfer.files); };
  const handleVideoInput = (e) => { if (e.target.files.length > 0) handleVideoFiles(e.target.files); };
  const handleRemoveVideo = (i) => setVideos((prev) => prev.filter((_, idx) => idx !== i));

  const onPostArticle = async () => {
    const f = addFormData;
    if (storeData?.active_plan === 1 && storeData?.total_products >= 30) {
      NotificationManager.warning(langJson?.plan_limit_products || "Free plan limited to 30 products. Upgrade to Pro to add more.");
      return;
    }
    if (!f.article_no) { setSignupStatus(langJson?.articlenoisrequired || "Article no. is required."); return; }
    if (!f.order_no) { setSignupStatus(langJson?.ordernoisrequired || "Order no. is required."); return; }
    if (!f.article_name) { setSignupStatus(langJson?.productnameisrequired || "Product name is required."); return; }
    if (!f.brand) { setSignupStatus(langJson?.brandisrequired || "Brand is required."); return; }
    if (!f.color) { setSignupStatus(langJson?.colorisrequired || "Colour is required."); return; }
    if (!f.article_description) { setSignupStatus(langJson?.articledescriptionisrequired || "Description is required."); return; }
    if (!f.unit_price) { setSignupStatus(langJson?.unitpriceisrequired || "Unit price is required."); return; }
    if (!/^[0-9,.]+$/.test(f.unit_price)) { setSignupStatus(langJson?.unitpriceacceptsonlynumbers || "Unit price: numbers only."); return; }
    if (sizeArray.length === 0) { setSignupStatus(langJson?.sizeisrequired || "At least one size is required."); return; }
    if (!f.price) { setSignupStatus(langJson?.priceisrequired || "Price is required."); return; }
    if (!/^[0-9,.]+$/.test(f.price)) { setSignupStatus(langJson?.priceacceptsonlynumbers || "Price: numbers only."); return; }
    if (!f.original_price) { setSignupStatus(langJson?.originalpriceisrequired || "Original price is required."); return; }
    if (!/^[0-9,.]+$/.test(f.original_price)) { setSignupStatus(langJson?.originalpriceacceptsonlynumbers || "Original price: numbers only."); return; }
    if (!f.main_category) { setSignupStatus(langJson?.maincategoryisrequired || "Main category is required."); return; }
    if (!f.sub_category) { setSignupStatus(langJson?.subcategoryisrequired || "Sub category is required."); return; }
    if (!f.show_category) { setSignupStatus(langJson?.showcategoryisrequired || "Show category is required."); return; }
    if (images.length === 0) { setSignupStatus(langJson?.pleaseselectaphoto || "Please select at least one photo."); return; }

    setAddBtnDis(true);
    setSignupStatus("");

    const fd = new FormData();
    images.forEach((item) => fd.append("images[]", item));
    videos.forEach((item) => fd.append("videos[]", item));
    fd.append("article_no", f.article_no);
    fd.append("order_no", f.order_no);
    fd.append("title", f.article_name);
    fd.append("brand", f.brand);
    fd.append("color", f.color);
    fd.append("description", f.article_description);
    fd.append("unit_price", f.unit_price);
    fd.append("currency", f.currency || "₹");
    fd.append("sale_price", f.price);
    fd.append("price", f.original_price);
    fd.append("season", f.main_category);
    fd.append("pro_cat_id", f.sub_category);
    fd.append("cat_id", f.show_category);
    fd.append("is_public", f.article_type === "public" ? 1 : 0);
    fd.append("stocks", JSON.stringify(sizeArray));
    fd.append("made_in_italy", f.made_in_italy || 0);
    fd.append("stock_type", f.stock_type || "in_stock");
    fd.append("estimated_delivery_date", f.estimated_delivery_date || "");
    fd.append("estimated_delivery_days", f.estimated_delivery_days || "");
    fd.append("ships_from", f.ships_from || "");
    fd.append("eligible_for", f.eligible_for || "");
    fd.append("details", f.details || "");
    fd.append("lang", selectedLanguage);

    try {
      const res = await fetch(`${API_BASE_URL}products`, {
        method: "POST", body: fd,
        headers: { Authorization: `Bearer ${user.authorisation.token}` },
      });
      const data = await res.json();
      if (data.res === true) {
        NotificationManager.success(data.msg);
        navigate("/products");
      } else {
        setSignupStatus(data.msg);
        NotificationManager.warning(data.msg);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setAddBtnDis(false);
    }
  };

  useEffect(() => { fetchSubCategory(); fetchShowSubCategory(); fetchStoreData(); }, []);

  const renderMadeInDropdown = () => (
    <select name="made_in" value={addFormData.made_in} onChange={handleChange}>
      <option value="">{langJson?.madein || "Made In"}</option>
      {countryGroups.map((group) =>
        group.countries.length > 1 ? (
          <optgroup key={group.group} label={group.group}>
            {group.countries.map((c) => (
              <option key={c.code} value={c.code}>{c.names[dropdownLang] || c.names["en"]}</option>
            ))}
          </optgroup>
        ) : (
          group.countries.map((c) => (
            <option key={c.code} value={c.code}>{c.names[dropdownLang] || c.names["en"]}</option>
          ))
        )
      )}
    </select>
  );

  const renderShipsFromDropdown = () => (
    <select name="ships_from" value={addFormData.ships_from} onChange={handleChange}>
      <option value="">{langJson?.shipsfrom || "Ships From"}</option>
      {countryGroups.map((group) =>
        group.countries.length > 1 ? (
          <optgroup key={group.group} label={group.group}>
            {group.countries.map((c) => (
              <option key={c.code} value={c.code}>{c.names[dropdownLang] || c.names["en"]}</option>
            ))}
          </optgroup>
        ) : (
          group.countries.map((c) => (
            <option key={c.code} value={c.code}>{c.names[dropdownLang] || c.names["en"]}</option>
          ))
        )
      )}
    </select>
  );

  return (
    <div>
      <div className="dashHeader"><VendorHeader /></div>
      <div className="adminDash">
        <Sidebar />
        <div className="rightSide">
          <h2>{langJson?.addproduct || "Add Product"}</h2>
          <ul className="addAddress">
            <li>
              <label>{langJson?.articleno || "Article no"}.</label>
              <input type="text" name="article_no" onChange={handleChange} value={addFormData.article_no} />
            </li>
            <li>
              <label>{langJson?.orderno || "Order no"}.</label>
              <input type="text" name="order_no" onChange={handleChange} value={addFormData.order_no} />
            </li>
            <li>
              <label>{langJson?.name || "Name"}</label>
              <input type="text" name="article_name" onChange={handleChange} value={addFormData.article_name} />
            </li>
            <li>
              <label>{langJson?.brand || "Brand"}</label>
              <input type="text" name="brand" onChange={handleChange} value={addFormData.brand} />
            </li>
            <li>
              <label>{langJson?.colour || "Colour"}</label>
              <input type="text" name="color" onChange={handleChange} value={addFormData.color} />
            </li>
            <li>
              <label>{langJson?.currency || "Currency"}</label>
              <select name="currency" onChange={handleChange} value={addFormData.currency}>
                {currency.map((item, i) => <option key={i} value={item}>{item}</option>)}
              </select>
            </li>
            <li className="fullWidth">
              <label>{langJson?.articledescription || "Article description"}</label>
              <textarea name="article_description" value={addFormData.article_description} onChange={handleChange} />
            </li>

            {sizeError && <span className="lgnStatus">{sizeError}</span>}
            <li className="sizeInput">
              <label>{langJson?.size || "Size"}:</label>
              <input type="text" name="size" value={addFormData.size} onChange={handleChange} />
              <button type="button" className="addBtn" onClick={addSize}>{langJson?.add || "Add"}</button>
              <span onClick={() => setAddFormData((p) => ({ ...p, article_type: "public" }))}>
                <input type="radio" name="articleType" readOnly checked={addFormData.article_type === "public"} />
                {langJson?.public || "Public"}
              </span>
              <span onClick={() => setAddFormData((p) => ({ ...p, article_type: "hidden" }))}>
                <input type="radio" name="articleType" readOnly checked={addFormData.article_type === "hidden"} />
                {langJson?.hidden || "Hidden"}
              </span>
              {sizeArray.map((item, index) => (
                <div className="sizeLoop" key={index}>
                  <div className="quan">
                    {item.size}
                    <img className="delIcon" src={deleteIcon} alt="remove" onClick={() => removeSize(index)} />
                  </div>
                  <input type="text" value={item.quantity} onChange={(e) => onQuantityChange(e, index)} />
                </div>
              ))}
            </li>

            <li>
              <label>{langJson?.price || "Price"}:</label>
              <input type="number" name="price" onChange={handleChange} value={addFormData.price} />
            </li>
            <li>
              <label>{langJson?.unitprice || "Unit price"}:</label>
              <input type="number" name="unit_price" onChange={handleChange} value={addFormData.unit_price} />
            </li>
            <li>
              <label>{langJson?.originalprice || "Original price"}:</label>
              <input type="number" name="original_price" onChange={handleChange} value={addFormData.original_price} />
            </li>

            <li>
              <label>{langJson?.maincategorygroupseasonarticle || "Main category – Group – Season"}:</label>
              <input type="text" name="main_category" onChange={handleChange} value={addFormData.main_category} />
            </li>
            <li>
              <label>{langJson?.subcategory || "Sub category"}:</label>
              <select name="sub_category" onChange={handleChange} value={addFormData.sub_category}>
                <option value="">{langJson?.subcategory || "Sub category"}</option>
                {subCategoryList.map((item, i) => (
                  <option key={i} value={item.id}>{langJson?.[item.slug] ?? item.title}</option>
                ))}
              </select>
            </li>
            <li>
              <label>{langJson?.showcategory || "Show category"}:</label>
              <select name="show_category" onChange={handleChange} value={addFormData.show_category}>
                <option value="">{langJson?.showcategory || "Show category"}</option>
                {showCategoryList.map((item, i) => (
                  <optgroup key={i} label={langJson?.[item.slug] ?? item.title}>
                    {item.sub_categories?.map((ch, ci) => (
                      <option key={ci} value={ch.id}>{langJson?.[ch.slug] ?? ch.title}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </li>
            <li>
              <label>{langJson?.madein || "Made In"}:</label>
              {renderMadeInDropdown()}
            </li>

            <li>
              <label>{langJson?.stocktype || "Stock Type"}:</label>
              <select name="stock_type" onChange={handleChange} value={addFormData.stock_type}>
                <option value="in_stock">{langJson?.stocktypeinstock || "In Stock"}</option>
                <option value="order">{langJson?.stocktypeorder || "Order"}</option>
              </select>
            </li>

            {addFormData.stock_type === "in_stock" && (
              <li>
                <label>{langJson?.estimateddeliverydays || "Estimated Delivery Days"}:</label>
                <input type="number" name="estimated_delivery_days" onChange={handleChange} value={addFormData.estimated_delivery_days} min="1" max="365" />
              </li>
            )}

            {addFormData.stock_type === "order" && (
              <li>
                <label>{langJson?.estimateddeliverydate || "Estimated Delivery Date"}:</label>
                <input type="date" name="estimated_delivery_date" onChange={handleChange} value={addFormData.estimated_delivery_date} />
              </li>
            )}

            <li>
              <label>{langJson?.shipsfrom || "Ships From"}:</label>
              {renderShipsFromDropdown()}
            </li>

            <li>
              <label>{langJson?.eligiblefor || "Eligible For"}:</label>
              <input type="text" name="eligible_for" onChange={handleChange} value={addFormData.eligible_for} />
            </li>

            <li className="fullWidth">
              <label>{langJson?.details || "Details"}:</label>
              <textarea name="details" onChange={handleChange} value={addFormData.details} rows="3" />
            </li>

            <li className="fullWidth">
              <label>{langJson?.uploadimages || "Upload images"}:</label>
              {imageError && <span className="lgnStatus">{imageError}</span>}
              <br />
              <input
                type="file" ref={imageBtnRef} hidden
                onChange={handleFileInput} multiple
                accept=".jpg,.jpeg,.png,.webp,.avif"
              />
              <button type="button" onClick={() => imageBtnRef.current.click()}>
                {langJson?.chooseimages || "Choose images"}
              </button>
              <div
                className="uploadFle"
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDrop}
              >
                {images.length === 0 && (
                  <span className="uploadFleHint">{langJson?.dragdropimages || "Drag & drop images"}</span>
                )}
                {images.map((item, index) => (
                  <div className="uploadThumbItem" key={index}>
                    <img className="uploadThumbImg zoomable-image" src={URL.createObjectURL(item)} alt="" onClick={() => {
                      setZoomImageIndex(index);
                      setZoomModalOpen(true);
                    }} style={{ cursor: "pointer" }} />
                    <img className="uploadThumbDelete" src={deleteIcon} alt="remove" onClick={() => handleRemoveImage(index)} />
                  </div>
                ))}
              </div>

              <div>
                {storeData && storeData?.active_plan !== 4 && (
                  <div style={{
                    padding: "12px 16px",
                    marginBottom: "16px",
                    background: "#000",
                    border: "1px solid #333",
                    borderRadius: "4px",
                    color: "#ecf0f1",
                    fontSize: "14px",
                  }}>
                    {langJson?.ai_enhancer_pro_ai_only || "AI Enhancer is available only with Pro AI plan. Upgrade to unlock this feature."}
                  </div>
                )}
                <div style={{ opacity: storeData?.active_plan === 4 ? 1 : 0.5, pointerEvents: storeData?.active_plan === 4 ? 'auto' : 'none' }}>
                  <AiEnhancePanel
                    images={images}
                    onReplaceImage={handleReplaceImage}
                    apiBaseUrl={API_BASE_URL}
                    token={user?.authorisation?.token}
                    langJson={langJson}
                  />
                </div>
              </div>
            </li>

            <li className="fullWidth">
              <label>{langJson?.uploadvideos || "Upload Videos"}:</label>
              {videoError && <span className="lgnStatus">{videoError}</span>}
              <br />
              <input
                type="file" ref={videoBtnRef} hidden
                onChange={handleVideoInput} multiple
                accept=".mp4,.webm,.mov,.avi,.mkv"
              />
              <button type="button" onClick={() => videoBtnRef.current.click()}>
                {langJson?.uploadvideo || "Choose video"}
              </button>
              <div
                className="uploadFle"
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleVideoDrop}
              >
                {videos.length === 0 && (
                  <span className="uploadFleHint">{langJson?.dragdropvideo || "Drag & drop video files"}</span>
                )}
                {videos.map((item, index) => (
                  <div className="uploadThumbItem" key={index}>
                    <div className="uploadVideoThumb">
                      <span>📹</span>
                      <p>{item.name.substring(0, 20)}</p>
                    </div>
                    <img className="uploadThumbDelete" src={deleteIcon} alt="remove" onClick={() => handleRemoveVideo(index)} />
                  </div>
                ))}
              </div>
            </li>

            <li className="fullWidth saveBtn">
              <button type="button" disabled={addBtnDis} onClick={onPostArticle}>
                {addBtnDis ? (langJson?.pleasewait || "Please wait…") : (langJson?.addproduct || "Add Product")}
              </button>
            </li>
            {signupStatus && <span className="lgnStatus">{signupStatus}</span>}
          </ul>
        </div>
      </div>

      {zoomModalOpen && (
        <div className="zoom-modal-overlay" onClick={() => setZoomModalOpen(false)}>
          <button className="zoom-modal-close" onClick={() => setZoomModalOpen(false)}>×</button>
          <div className="zoom-modal" onClick={e => e.stopPropagation()}>
            <div className="zoom-image-container">
              <img
                src={URL.createObjectURL(images[zoomImageIndex])}
                alt=""
              />
            </div>

            <div className="zoom-thumbnails">
              {images.map((img, i) => (
                <div
                  key={i}
                  className={`zoom-thumb ${zoomImageIndex === i ? "active" : ""}`}
                  onClick={() => setZoomImageIndex(i)}
                >
                  <img src={URL.createObjectURL(img)} alt="" />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <VendorFooter />
    </div>
  );
};

export default AddProduct;